import { ApqModel } from "../../domain/ApqModel";
import { SectionProps } from "./SectionProps";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { Select } from "@ofqual/portal-components/components/Select";
import { Controller } from "react-hook-form";
import { Error } from "../common/Error";
import { Grid } from "@material-ui/core";
import { fieldLabelsOfq } from "../sections/fieldLabels";
import {
  cachedApqEngagementActivityService,
  cachedApqSourceTypeService,
} from "../../services";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { ContactTypeSection } from "./ContactTypeSection";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { ExperimentalFeature } from "@ofqual/portal-components/components/ExperimentalFeature";
import { useSelector } from "react-redux";
import { authSelector } from "../../redux/selectors";

export const EngagementActivity: React.FC<{
  sectionProps: SectionProps<ApqModel>;
}> = ({ sectionProps }) => {
  const { control, errors, disabled, clearErrors } = sectionProps;
  const { experimentalFeatures } = useSelector(authSelector);

  const [apqEngagementLookups] = useLookup({
    service: cachedApqEngagementActivityService,
    id: "id",
    label: "value",
  });
  const [apqSourceTypeLookups] = useLookup({
    service: cachedApqSourceTypeService,
    id: "id",
    label: "value",
  });

  return (
    <Grid container spacing={3}>
      <ContactTypeSection {...sectionProps} />
      <Grid item xs={12}>
        <Controller
          control={control}
          name="engagementActivityId"
          render={({ onChange, ...props }) => (
            <Select
              required
              label={fieldLabelsOfq.engagementActivity}
              disabled={disabled}
              datatest="engagementActivityId-field"
              onChange={(e) => {
                if (e.target.value !== null) {
                  clearErrors("engagementActivityId");
                }
                onChange(e);
              }}
              {...props}
            >
              {apqEngagementLookups}
            </Select>
          )}
        />
        {errors.engagementActivityId?.type === "required" && (
          <Error data-test="engagementActivity-validation">
            This field is required
          </Error>
        )}
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="engagementActivityDescription"
          render={({ onChange, ...props }) => (
            <TextField
              multiline
              label={fieldLabelsOfq.Description}
              disabled={disabled}
              required
              data-test="engagementActivityDescription-field"
              onChange={(e) => {
                if (e.target.value !== null) {
                  clearErrors("engagementActivityDescription");
                }
                onChange(e);
              }}
              {...props}
            >
              {apqSourceTypeLookups}
            </TextField>
          )}
        />
        {errors.engagementActivityDescription?.type === "required" && (
          <Error data-test="engagementActivityDescription-validation">
            This field is required
          </Error>
        )}
      </Grid>
      <ExperimentalFeature
        experimentalModuleName="portal-apq"
        experimentalFeatures={experimentalFeatures}
        experimentalFeatureVisibleName="apq-notes"
      >
        <Grid item xs={12}>
          <Controller
            control={control}
            name="notes"
            render={({ onChange, value }) => (
              <>
                <label>
                  {fieldLabelsOfq.notes} {disabled ? "(Read Only)" : ""}
                </label>
                <MarkdownEditor
                  onChange={onChange}
                  value={value}
                  readOnly={disabled}
                  dataTest="notes-field"
                />
              </>
            )}
          />
        </Grid>
      </ExperimentalFeature>
    </Grid>
  );
};
